import type { MatrixCell, MatrixRow, MatrixSupplier } from 'pages/prices/utils/build-price-matrix';
import { daysSince } from 'pages/prices/utils/matrix-display';

export function pricedCellCount(row: MatrixRow): number {
  return Object.values(row.cells).filter((cell) => cell?.price != null).length;
}

// Alphabetical, brand first then spec (Sadia 0.9, Sadia 1.0, Sadia 1.1,
// Seara 0.9…), origin/state/id as tie-breakers. Numeric-aware so "2 kg"
// precedes "10 kg" and "0.9" precedes "1.0"; case-insensitive; a blank key
// trails every non-blank one so unbranded variants close the list.
const SORT_KEYS = ['brand', 'spec', 'origin', 'state'] as const;

function compareText(a: string | null, b: string | null): number {
  const left = a?.trim() ?? '';
  const right = b?.trim() ?? '';
  if (!left || !right) return Number(!left) - Number(!right);
  return left.localeCompare(right, undefined, { numeric: true, sensitivity: 'base' });
}

export type SortDirection = 'asc' | 'desc';

export function sortRowsByBrandSpec(rows: MatrixRow[], direction: SortDirection = 'asc'): MatrixRow[] {
  const sorted = [...rows].sort((a, b) => {
    for (const key of SORT_KEYS) {
      const order = compareText(a.variant[key], b.variant[key]);
      if (order) return order;
    }
    return a.variant.id - b.variant.id;
  });
  return direction === 'desc' ? sorted.reverse() : sorted;
}

// The header sort button writes `?sort=brand.asc|desc` (BaseSortButton
// contract); anything else is the default A→Z order.
export function sortDirectionFromParam(sort: unknown): SortDirection {
  return sort === 'brand.desc' ? 'desc' : 'asc';
}

export interface MatrixViewFilter {
  // Keep only priced cells.
  pricedOnly: boolean;
  // Keep only prices updated within the last N days; null = off.
  recentDays: number | null;
  // Rows shown whole regardless of the filters — variants the operator just
  // created here, which have no price yet to match on.
  pinnedVariantIds?: Set<number>;
}

const isFilterActive = (filter: MatrixViewFilter): boolean => filter.pricedOnly || filter.recentDays != null;

// A price with an unknown timestamp never matches a recency window — we
// can't claim a recency we don't have.
function cellMatches(cell: MatrixCell | undefined, filter: MatrixViewFilter, now: number): cell is MatrixCell {
  if (cell?.price == null) return false;
  if (filter.recentDays != null) {
    const age = daysSince(cell.lastModifiedAt, now);
    if (age == null || age > filter.recentDays) return false;
  }
  return true;
}

// The view filters act on the CELL grid, not on rows: a 17-day-old price is
// not part of the "updated in last 7 days" table even when its row stays for
// a fresher one. Non-matching cells are dropped (they render as empty), rows
// with no matching cell hide, and suppliers with no matching cell across the
// kept rows lose their column. `isLowest` is recomputed among the surviving
// cells so a dropped price can't hold the flag.
export function applyMatrixViewFilter(
  rows: MatrixRow[],
  suppliers: MatrixSupplier[],
  filter: MatrixViewFilter,
  now: number = Date.now()
): { rows: MatrixRow[]; suppliers: MatrixSupplier[] } {
  if (!isFilterActive(filter)) return { rows, suppliers };

  const filteredRows = rows
    .map((row) => {
      if (filter.pinnedVariantIds?.has(row.variant.id)) return row;

      const cells: Record<number, MatrixCell | undefined> = {};
      for (const [supplierId, cell] of Object.entries(row.cells)) {
        if (cellMatches(cell, filter, now)) cells[Number(supplierId)] = { ...cell, isLowest: false };
      }

      const prices = Object.values(cells)
        .map((cell) => cell?.price)
        .filter((price): price is number => price != null);
      if (prices.length) {
        const min = Math.min(...prices);
        for (const cell of Object.values(cells)) {
          if (cell && cell.price === min) cell.isLowest = true;
        }
      }

      return { variant: row.variant, cells };
    })
    .filter((row) => Object.keys(row.cells).length > 0 || filter.pinnedVariantIds?.has(row.variant.id));

  const keptSupplierIds = new Set<number>();
  filteredRows.forEach((row) => Object.keys(row.cells).forEach((id) => keptSupplierIds.add(Number(id))));

  return { rows: filteredRows, suppliers: suppliers.filter((supplier) => keptSupplierIds.has(supplier.id)) };
}
